import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { 
  onAuthStateChanged, 
  signInWithPopup, 
  GoogleAuthProvider, 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut
} from 'firebase/auth';
import { doc, getDoc, setDoc, collection, addDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
// Removed Google Identity Services - using Firebase auth only
import { initializeAuth, getCurrentUser, restoreScopedAccount } from './services/authorizationService';
import { initializeFeatureFlags } from './services/featureFlags';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sign in with Google using Firebase Auth
  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // Create user profile in Firestore if it doesn't exist
      await createUserProfile(result.user);
      
      return result.user;
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  // Create or update user profile in Firestore
  const createUserProfile = useCallback(async (user) => {
    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);
      
      if (!userDocSnap.exists()) {
        // Create new user profile
        const userData = {
          name: user.displayName || user.email,
          email: user.email,
          displayName: user.displayName || user.email,
          photoURL: user.photoURL || null,
          role: 'CLIENT', // Use uppercase to match RBAC constants
          status: 'active',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          reminders: [], // Initialize empty reminders array
          apiData: {} // Initialize empty API data object
        };
        
        await setDoc(userDocRef, userData);
        console.log('New user profile created');

        // Create account for the client (if not already exists)
        const accountData = {
          name: user.displayName || user.email,
          description: `Account for ${user.displayName || user.email}`,
          contactEmail: user.email,
          status: 'active',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        const accountRef = await addDoc(collection(db, 'accounts'), accountData);
        
        // Update user with account information
        await updateDoc(userDocRef, {
          accountId: accountRef.id,
          accountName: accountData.name,
          updatedAt: new Date().toISOString()
        });
        
        console.log('Account created for user:', accountRef.id);
      } else {
        // Update existing profile
        await setDoc(userDocRef, {
          ...userDocSnap.data(),
          email: user.email,
          displayName: user.displayName || user.email,
          photoURL: user.photoURL || null,
          updatedAt: new Date().toISOString()
        }, { merge: true });
        console.log('User profile updated');
      }
    } catch (error) {
      console.error('Error creating/updating user profile:', error);
    }
  }, []);

  // Removed Google Identity Services sign-in - using Firebase auth only

  // Sign up with email/password
  const signUpWithEmail = async (email, password) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Create user profile in Firestore
      await createUserProfile(result.user);
      
      return result.user;
    } catch (error) {
      console.error('Error signing up with email:', error);
      throw error;
    }
  };

  // Sign in with email/password
  const signInWithEmail = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result.user;
    } catch (error) {
      console.error('Error signing in with email:', error);
      throw error;
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      // Clear any Google user data from localStorage
      localStorage.removeItem('google_user');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Listen for authentication state changes
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed:', user ? `User ${user.email} signed in` : 'No user');
      setCurrentUser(user);

      if (user) {
        // Initialize authorization service
        try {
          console.log('🔐 Initializing authorization for user:', user.uid, user.email);
          await initializeAuth(user);
          
          // Restore scoped account from localStorage if user is admin
          restoreScopedAccount();
          
          const authUser = getCurrentUser();
          console.log('👤 Authorization result:', authUser);
          if (authUser) {
            setRole(authUser.role);
            setProfileData({
              email: authUser.email,
              role: authUser.role,
              accountId: authUser.accountId,
              accountName: authUser.accountName
            });
            console.log('✅ User role set to:', authUser.role);
          }
        } catch (error) {
          console.error('Error initializing authorization:', error);
        }

        // Load additional user profile data if needed
        try {
          const userDocRef = doc(db, 'users', user.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            
            // Check if user account is suspended
            if (userData.status === 'suspended') {
              console.log('User account is suspended, redirecting to suspended page');
              window.location.href = '/suspended';
              return;
            }
            
            setProfileData(prev => ({
              ...prev,
              ...userData
            }));
          }
        } catch (error) {
          console.error('Error loading user data:', error);
        }
      } else {
        // Clear authorization state
        await initializeAuth(null);
        setProfileData(null);
        setRole(null);
      }

      setLoading(false);
    });

    return unsub;
  }, []);

  // Initialize feature flags on app start
  useEffect(() => {
    initializeFeatureFlags();
  }, []);

  // Removed Google Identity Services check - using Firebase auth only

  const value = {
    currentUser,
    profileData,
    role,
    loading,
    isAuthenticated: !!currentUser,
    signInWithGoogle,
    signUpWithEmail,
    signInWithEmail,
    signOut,
    createUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
